import React, { useMemo, useEffect } from 'react'
import Table from '../../components/Table'
import { connect } from 'react-redux'
import Modal from '../../components/Modal';
import { getMyCellsRequested, updateCellRequested } from '../../actions/cellAction';
import PropTypes from 'prop-types';
import { OfficeBuildingIcon } from '@heroicons/react/outline';
import { HomeIcon, UserGroupIcon } from '@heroicons/react/solid';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import SearchableSelect from '../../components/SearchableSelect';
import CellMap from '../../components/CellMap';

const MyCells = ({
    user,
    myCells,
    myCellsGetRequested,
    getMyCellsRequested: getMyCells,
    updateCellRequested: updateCell,
}) => {
    const [showModal, setShowModal] = React.useState(false);
    const [formData, setFormData] = React.useState({});
    const navigate = useNavigate();

    useEffect(() => {
        if (!myCellsGetRequested) {
            getMyCells();
        }
    }, []);

    const columns = useMemo(() => [
        {
            Header: "#",
            Cell: ({ cell }) => (
                <div className="flex items-center space-x-4">
                    <p>{cell.row.index + 1}</p>
                </div>
            ),
        },
        {
            Header: "Name",
            accessor: 'name',
        },
        {
            Header: "Address",
            accessor: 'address',
        },
        {
            Header: "City",
            accessor: 'city',
            Cell: ({ value }) => <div className="text-sm text-gray-500">{value || '—'}</div>,
        },
        {
            Header: "Cell Type",
            accessor: 'cell_type',
        },
        {
            Header: "Meeting Day",
            accessor: 'meeting_day',
        },
        {
            Header: "Meeting Time",
            accessor: 'meeting_time',
        },
        {
            Header: "Action",
            accessor: 'id',
            Cell: ({ cell }) => (
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => handleEditCell(cell.row.original)}
                        className="px-3 py-1 uppercase leading-wide font-bold text-xs rounded-full shadow-sm bg-yellow-100 text-yellow-800"
                    >Edit</button>
                </div>
            ),
        }
    ], []);

    if (!user || !user.id) {
        navigate('/login');
        return;
    }

    const notify = (msg) => toast.success(msg, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: "light",
    });

    const handleEditCell = (data) => {
        setFormData(data);
        setShowModal(true);
    }

    const handleCloseModal = (e) => {
        e.preventDefault();
        setFormData({});
        setShowModal(false);
    };

    const handleUpdateCell = (e) => {
        e.preventDefault();
        updateCell(formData);
        notify("Cell updated successfully!");
        setShowModal(false);
        setFormData({});
    }

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setFormData(values => ({ ...values, [name]: value }))
    }

    const totalCells = myCells.length;
    const homeCells = myCells.filter(c => c.cell_type === 'Home Cell').length;
    const campusCells = myCells.filter(c => c.cell_type === 'Campus Cell').length;
    const familyCells = myCells.filter(c => c.cell_type === 'Family Cell').length;

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900">
            <Modal
                title="Edit cell details"
                size="lg"
                submitLabel="Save changes"
                onClose={handleCloseModal}
                show={showModal}
                onSubmit={handleUpdateCell}
            >
                <form className='grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2' onSubmit={(e) => e.preventDefault()}>
                    <div className="sm:col-span-2">
                        <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Name</label>
                        <div className="mt-2">
                            <input
                                id="name"
                                name="name"
                                type="text"
                                required
                                className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                value={formData?.name || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium leading-6 text-gray-900">Address</label>
                        <div className="mt-2">
                            <input
                                id="address"
                                name="address"
                                type="text"
                                required
                                className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                value={formData?.address || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="city" className="block text-sm font-medium leading-6 text-gray-900">City</label>
                        <div className="mt-2">
                            <input
                                id="city"
                                name="city"
                                type="text"
                                autoComplete="address-level2"
                                className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                value={formData?.city || ""}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="meeting_day" className="block text-sm font-medium leading-6 text-gray-900">Meeting Day</label>
                        <div className="mt-2">
                            <SearchableSelect
                                name="meeting_day"
                                value={formData?.meeting_day || ""}
                                onChange={handleChange}
                                placeholder="Select an option"
                                options={[
                                    { value: 'Monday', label: 'Monday' },
                                    { value: 'Tuesday', label: 'Tuesday' },
                                    { value: 'Thurday', label: 'Thursday' },
                                    { value: 'Saturday', label: 'Saturday' },
                                ]}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="cell_type" className="block text-sm font-medium leading-6 text-gray-900">Cell Type</label>
                        <div className="mt-2">
                            <SearchableSelect
                                name="cell_type"
                                value={formData?.cell_type || ""}
                                onChange={handleChange}
                                placeholder="Select an option"
                                options={[
                                    { value: 'Campus Cell', label: 'Campus Cell' },
                                    { value: 'Home Cell', label: 'Home Cell' },
                                    { value: 'Family Cell', label: 'Family Cell' },
                                    { value: 'inactive', label: 'Inactive Cell' },
                                ]}
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="meeting_time" className="block text-sm font-medium leading-6 text-gray-900">Meeting Time</label>
                        <div className="mt-2">
                            <input className="px-2 py-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                                required
                                name="meeting_time"
                                type="time"
                                value={formData?.meeting_time || ""}
                                onChange={handleChange} />
                        </div>
                    </div>
                </form>
            </Modal>
            <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">My Cells</h1>
                        <p className="text-gray-500 mt-1">View and edit your cell group details</p>
                    </div>
                </div>

                {/* Stats Cards */}
                {myCells.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <StatCard
                            title="Total Cells"
                            value={totalCells}
                            icon={<UserGroupIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-emerald-500"
                        />
                        <StatCard
                            title="Home Cells"
                            value={homeCells}
                            icon={<HomeIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-blue-500"
                        />
                        <StatCard
                            title="Campus Cells"
                            value={campusCells}
                            icon={<UserGroupIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-purple-500"
                        />
                        <StatCard
                            title="Family Cells"
                            value={familyCells}
                            icon={<HomeIcon className="h-6 w-6 text-white" />}
                            bgColor="bg-orange-500"
                        />
                    </div>
                )}

                {/* Table */}
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                    {myCells.length > 0 ? (
                        <div className="overflow-x-auto">
                            <Table columns={columns} data={myCells} />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16">
                            <OfficeBuildingIcon className="h-16 w-16 text-gray-300 mb-4" />
                            <h2 className="text-xl font-semibold text-gray-700">No cells assigned</h2>
                            <p className="text-gray-500 mt-1">You are not currently assigned to any cells</p>
                        </div>
                    )}
                </div>

                {/* Map */}
                {myCells.length > 0 && (
                    <div className="mt-6 bg-white shadow-lg rounded-lg overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900">Cell Locations</h2>
                        </div>
                        <CellMap cells={myCells} height={380} />
                    </div>
                )}
            </main>
        </div>
    );
}

const StatCard = ({ title, value, icon, bgColor }) => {
    return (
        <div className="bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">{title}</p>
                    <p className="text-3xl font-bold text-gray-900">{value}</p>
                </div>
                <div className={`${bgColor} rounded-full p-3`}>
                    {icon}
                </div>
            </div>
        </div>
    );
};

MyCells.propTypes = {
    myCells: PropTypes.array,
    myCellsGetRequested: PropTypes.bool,
    getMyCellsRequested: PropTypes.func.isRequired,
    updateCellRequested: PropTypes.func.isRequired,
};

MyCells.defaultProps = {
    myCells: [],
    myCellsGetRequested: false,
};

const mapStateToProps = (state) => ({
    myCells: state.cell.myCells,
    myCellsGetRequested: state.cell.myCellsGetRequested,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    getMyCellsRequested,
    updateCellRequested,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MyCells);
